import type { ChatMessage, ChatParams, Conversation, ConversationSummary, KnowledgeScope } from "@/types";
import { DEFAULT_KNOWLEDGE_SCOPE } from "@/types";
import { logger } from "@/services/logger";
import type { ConversationRepository } from "../ConversationRepository";
import { getDb } from "./connection";

const log = logger.scope("sqlite-conversations");

interface ConversationRow {
  id: string;
  title: string;
  created_at: number;
  updated_at: number;
  model_id: string | null;
  params_json: string;
  title_generated: number;
  knowledge_scope_json: string;
  workspace_id: string | null;
  pinned: number;
  folder_id: string | null;
  parent_conversation_id: string | null;
  branch_point_seq: number | null;
}

interface ConversationSummaryRow {
  id: string;
  title: string;
  created_at: number;
  updated_at: number;
  model_id: string | null;
  message_count: number;
  workspace_id: string | null;
  pinned: number;
  folder_id: string | null;
}

interface MessageRow {
  id: string;
  role: ChatMessage["role"];
  content: string;
  created_at: number;
  status: ChatMessage["status"];
  error: string | null;
  favorite: number;
  images_json: string;
}

function parseKnowledgeScope(json: string): KnowledgeScope {
  try {
    return JSON.parse(json) as KnowledgeScope;
  } catch {
    return { ...DEFAULT_KNOWLEDGE_SCOPE };
  }
}

function rowToConversation(row: ConversationRow, messages: ChatMessage[]): Conversation {
  return {
    id: row.id,
    title: row.title,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    modelId: row.model_id,
    params: JSON.parse(row.params_json) as ChatParams,
    messages,
    titleGenerated: row.title_generated === 1,
    knowledgeScope: parseKnowledgeScope(row.knowledge_scope_json),
    workspaceId: row.workspace_id,
    pinned: row.pinned === 1,
    folderId: row.folder_id,
    parentConversationId: row.parent_conversation_id,
    branchPointSeq: row.branch_point_seq,
  };
}

function rowToMessage(row: MessageRow): ChatMessage {
  let images: ChatMessage["images"];
  try {
    const parsed = JSON.parse(row.images_json) as ChatMessage["images"];
    images = parsed && parsed.length > 0 ? parsed : undefined;
  } catch {
    images = undefined;
  }
  return {
    id: row.id,
    role: row.role,
    content: row.content,
    createdAt: row.created_at,
    status: row.status,
    error: row.error ?? undefined,
    images,
    favorite: row.favorite === 1,
  };
}

export const sqliteConversationRepository: ConversationRepository = {
  async list() {
    const db = await getDb();
    const rows = await db.select<ConversationSummaryRow[]>(
      `SELECT c.id, c.title, c.created_at, c.updated_at, c.model_id, c.workspace_id, c.pinned, c.folder_id,
              COUNT(m.id) AS message_count
       FROM conversations c
       LEFT JOIN messages m ON m.conversation_id = c.id
       GROUP BY c.id
       ORDER BY c.updated_at DESC`,
    );
    return rows.map(
      (r): ConversationSummary => ({
        id: r.id,
        title: r.title,
        createdAt: r.created_at,
        updatedAt: r.updated_at,
        modelId: r.model_id,
        messageCount: r.message_count,
        workspaceId: r.workspace_id,
        pinned: r.pinned === 1,
        folderId: r.folder_id,
      }),
    );
  },

  async get(id) {
    const db = await getDb();
    const rows = await db.select<ConversationRow[]>("SELECT * FROM conversations WHERE id = $1", [id]);
    const row = rows[0];
    if (!row) return null;

    const messageRows = await db.select<MessageRow[]>(
      "SELECT id, role, content, created_at, status, error, favorite, images_json FROM messages WHERE conversation_id = $1 ORDER BY seq ASC",
      [id],
    );
    return rowToConversation(row, messageRows.map(rowToMessage));
  },

  async save(conversation) {
    const db = await getDb();
    await db.execute("BEGIN");
    try {
      await db.execute(
        `INSERT OR REPLACE INTO conversations
          (id, title, created_at, updated_at, model_id, params_json, title_generated, knowledge_scope_json,
           workspace_id, pinned, folder_id, parent_conversation_id, branch_point_seq)
         VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9, $10, $11, $12, $13)`,
        [
          conversation.id,
          conversation.title,
          conversation.createdAt,
          conversation.updatedAt,
          conversation.modelId,
          JSON.stringify(conversation.params),
          conversation.titleGenerated ? 1 : 0,
          JSON.stringify(conversation.knowledgeScope),
          conversation.workspaceId,
          conversation.pinned ? 1 : 0,
          conversation.folderId,
          conversation.parentConversationId,
          conversation.branchPointSeq,
        ],
      );

      // The interface's contract is a full upsert of the current record, so
      // messages are always the complete authoritative list — replace wholesale
      // rather than diffing.
      await db.execute("DELETE FROM messages WHERE conversation_id = $1", [conversation.id]);
      for (const [seq, message] of conversation.messages.entries()) {
        await db.execute(
          `INSERT INTO messages (id, conversation_id, seq, role, content, created_at, status, error, favorite, images_json)
           VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9, $10)`,
          [
            message.id,
            conversation.id,
            seq,
            message.role,
            message.content,
            message.createdAt,
            message.status,
            message.error ?? null,
            message.favorite ? 1 : 0,
            JSON.stringify(message.images ?? []),
          ],
        );
      }
      await db.execute("COMMIT");
    } catch (err) {
      log.error("Failed to save conversation; rolling back", err);
      await db.execute("ROLLBACK").catch(() => undefined);
      throw err;
    }
  },

  async delete(id) {
    const db = await getDb();
    await db.execute("DELETE FROM conversations WHERE id = $1", [id]);
  },
};
